import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import * as api from '../../api';
import { Btn, Input, Modal, Select } from '../UI';
import { Panel } from '../materialTakeoff/MTOShared';

const th = { padding: '10px 12px', fontSize: 10, fontWeight: 800, color: '#fff', background: '#0b318bff', border: '1px solid black',
             textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', textAlign: 'left' };
const td = { padding: '8px 12px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)' };

// The project's prefix, and the discipline codes shared by every project.
export default function CGSettings({ projectId, project, settings, disciplines, onChanged, canEdit, isSuper, toast }) {
  const [prefix, setPrefix] = useState('');
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);     // discipline being added / edited

  useEffect(() => { setPrefix(settings?.prefix || settings?.suggested_prefix || ''); }, [settings]);
  useEffect(() => {
    api.listDmrCategories(projectId || undefined).then(c => setCategories(Array.isArray(c) ? c : (c?.data || []))).catch(() => setCategories([]));
  }, [projectId]);

  const savePrefix = async () => {
    setBusy(true);
    try { await api.setCodePrefix(projectId, prefix.trim().toUpperCase()); toast?.show?.('Prefix saved', 'success'); onChanged(); }
    catch (e) { toast?.show?.(e.message || 'Could not save the prefix', 'error'); }
    setBusy(false);
  };

  const saveDiscipline = async () => {
    const body = { code: editing.code.trim().toUpperCase(), name: editing.name.trim(), dmr_category_id: editing.dmr_category_id || null,
                   sort_order: Number(editing.sort_order) || 0, is_active: editing.is_active };
    try {
      if (editing.id) await api.updateCodeDiscipline(editing.id, body); else await api.addCodeDiscipline(body);
      toast?.show?.('Discipline saved', 'success');
      setEditing(null);
      onChanged();
    } catch (e) {
      toast?.show?.(e.message || 'Could not save the discipline', 'error');
    }
  };

  const clean = prefix.trim().toUpperCase();
  const valid = /^[A-Z][A-Z0-9]{1,9}$/.test(clean);

  return (
    <div>
      {projectId ? (
        <Panel title={`Code prefix — ${project?.project_name || ''}`}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ width: 200 }}>
              <Input label="Prefix (2–10 letters/digits)" value={prefix} onChange={v => setPrefix(v.toUpperCase())} disabled={!canEdit || settings?.locked} maxLength={10} />
            </div>
            {canEdit && !settings?.locked && <Btn onClick={savePrefix} loading={busy} disabled={!valid || clean === settings?.prefix}>Save Prefix</Btn>}
            <div style={{ fontSize: 13 }}>
              Codes will look like <b style={{ fontFamily: 'monospace' }}>{valid ? clean : '???'}-ELECT-0001</b>, sub-codes <b style={{ fontFamily: 'monospace' }}>{valid ? clean : '???'}-ELECT-0001-01</b>
            </div>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '10px 0 0' }}>
            {settings?.locked
              ? `${settings.codes_issued} code(s) have been issued with this prefix, so it can no longer change.`
              : `Suggested from the project code: ${settings?.suggested_prefix || '—'}. Each project needs its own prefix; it is fixed once the first code is issued.`}
          </p>
        </Panel>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>Select a project above to set its code prefix.</div>
      )}

      <Panel title="Discipline codes (all projects)"
             right={isSuper && <Btn variant="secondary" onClick={() => setEditing({ code: '', name: '', dmr_category_id: '', sort_order: disciplines.length, is_active: true })}><Plus size={14} /> Add Discipline</Btn>}>
        <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Code', 'Name', 'Linked BOQ Discipline (category)', 'Codes Issued', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {disciplines.map(d => (
                <tr key={d.id}>
                  <td style={{ ...td, fontFamily: 'monospace', fontWeight: 800, color: '#0b318b' }}>{d.code}</td>
                  <td style={td}>{d.name}</td>
                  <td style={td}>{d.dmr_category_name || <span style={{ color: '#b45309' }}>not linked</span>}</td>
                  <td style={td}>{d.codes_issued}</td>
                  <td style={td}>{d.is_active ? 'Active' : 'Inactive'}</td>
                  <td style={td}>{isSuper && <Btn style={{ padding: '4px 10px', fontSize: 11.5 }} variant="secondary" onClick={() => setEditing({ ...d, dmr_category_id: d.dmr_category_id || '' })}>Edit</Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '10px 0 0' }}>
          Linking a code discipline to a BOQ discipline lets <b>Apply to BOQ</b> pick the right code for each line. A code that has been used cannot be renamed.
          {!isSuper && ' Only a super admin can change discipline codes.'}
        </p>
      </Panel>

      {editing && (
        <Modal title={editing.id ? `Edit ${editing.code}` : 'Add discipline code'} onClose={() => setEditing(null)} width={520} blur={2}>
          <div style={{ display: 'grid', gap: 10 }}>
            <Input label="Code (e.g. ELECT)" value={editing.code} maxLength={10} onChange={v => setEditing(p => ({ ...p, code: v.toUpperCase() }))}
                   disabled={!!editing.id && editing.codes_issued > 0} />
            <Input label="Name" value={editing.name} onChange={v => setEditing(p => ({ ...p, name: v }))} />
            <Select label="Linked BOQ discipline (category)" value={editing.dmr_category_id} onChange={v => setEditing(p => ({ ...p, dmr_category_id: v }))}
                    options={[{ value: '', label: '— not linked —' }, ...categories.map(c => ({ value: c.id, label: c.name }))]} />
            <Input label="Order" type="number" value={editing.sort_order} onChange={v => setEditing(p => ({ ...p, sort_order: v }))} />
            <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={editing.is_active} onChange={e => setEditing(p => ({ ...p, is_active: e.target.checked }))} /> Active
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <Btn variant="secondary" onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn onClick={saveDiscipline} disabled={!editing.code.trim() || !editing.name.trim()}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
